import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import {
  ArrowLeft,
  ChevronDown,
  CopyIcon,
  EyeIcon,
  LeafIcon,
  LucideTriangleAlert,
  MapIcon,
  MapPin,
  Microscope,
  Navigation,
  Pencil,
  TagIcon,
} from "lucide-react"
import { Suspense, useCallback, useMemo } from "react"

import { useProjectData } from "@/contexts/ProjectData"
import { useCollection } from "@/hooks/useCollection"
import { useDisplayDistance } from "@/hooks/useDisplayDistance"
import {
  ObservationWithSyncStatus,
  useObservation,
} from "@/hooks/useObservation"
import { usePhotosForEntity } from "@/hooks/usePhotosForEntity"
import { useSpecies } from "@/hooks/useSpecies"

import { PhotoGrid } from "@/components/common/PhotoGrid"
import { SyncErrorBanner } from "@/components/common/SyncErrorBanner"
import { SyncStatusBadge } from "@/components/common/SyncStatusBadge"
import { getFormattedCustomFields } from "@/components/observations/utils"
import { useActiveNavTarget } from "@/hooks/useActiveNavTarget"
import {
  CollectionReference,
  CoordinateDisplay,
  IndeterminateMarker,
  TaxonName,
} from "@fieldbook/common/components"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@fieldbook/ui/dropdown-menu"
import { useALASpeciesDetail } from "@fieldbook/common/hooks"
import { Person } from "@fieldbook/common/types"
import { parsePostGISPoint } from "@fieldbook/common/utils"
import { Button } from "@fieldbook/ui/button"
import { IconPopover } from "@fieldbook/ui/popover"
import { Spinner } from "@fieldbook/ui/spinner"
import * as z from "zod"
import { useObservationActions } from "@/components/project/ProjectDataSheet/Observations/useObservationActions"
import { useSpeciesForObservation } from "@/hooks/useSpeciesForObservation"

const searchSchema = z.object({
  referrerType: z.union([z.literal("site")]).optional(),
  referrerId: z.string().optional(),
})

export const Route = createFileRoute(
  "/_private/projects/$id/data/observations/$observationId/",
)({
  component: ObservationDetailRoute,
  validateSearch: (search) => searchSchema.parse(search),
})

function ObservationDetailRoute() {
  const navigate = Route.useNavigate()
  const { id } = Route.useParams()
  const { referrerType, referrerId } = Route.useSearch()

  const handleBack = () => {
    if (referrerType === "site" && referrerId) {
      navigate({
        to: "/projects/$id/data/sites/$siteId",
        params: { id, siteId: referrerId },
        search: { tab: "observations" },
      })
    } else {
      navigate({ to: "/projects/$id/data/observations", params: { id } })
    }
  }

  return (
    <div className="flex flex-col">
      <button
        onClick={handleBack}
        className="text-muted-foreground mb-4 mt-2 flex items-center gap-1 text-sm focus-visible:outline-0"
      >
        <ArrowLeft size={12} />
        <span>Back</span>
      </button>

      <ObservationDetail />
    </div>
  )
}

const isExistingCollectionGuard = (
  obs: ObservationWithSyncStatus,
): obs is ObservationWithSyncStatus & {
  collection_id: string
  is_collection: false
} => Boolean(obs.collection_id && !obs.is_collection)

const ObservationActions = ({
  observation,
}: {
  observation: ObservationWithSyncStatus
}) => {
  const actions = useObservationActions(observation)
  const [, setActiveNavTarget] = useActiveNavTarget()
  const navigate = useNavigate()

  const handleNavigateTo = useCallback(() => {
    setActiveNavTarget({
      id: observation?.id,
      type: "observation",
    })
    navigate({ to: "/projects/$id", params: { id: observation.project_id } })
  }, [navigate, observation.id, observation.project_id, setActiveNavTarget])

  const { data: speciesArray } = useSpeciesForObservation(observation)
  const species = speciesArray?.[0]

  const handleViewSpecies = () => {
    const speciesId = species?.id
    if (!speciesId) return
    navigate({
      to: "/projects/$id/data/species/$speciesId",
      params: { id: observation.project_id, speciesId },
    })
  }

  const hasCollection = isExistingCollectionGuard(observation)
  const handleViewCollection = () => {
    if (!hasCollection) return
    navigate({
      to: "/projects/$id/data/observations/$observationId",
      params: {
        id: observation.project_id,
        observationId: observation.collection_id,
      },
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={"outline"}
          size="default"
          className="text-md flex items-center gap-1 space-x-2 transition-all duration-500 ease-in-out"
        >
          Actions
          <ChevronDown aria-label="Dropdown" size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="text-md">
        <DropdownMenuItem
          onClick={handleNavigateTo}
          className="items-center space-x-2"
        >
          <Navigation className="size-4" />
          <span>Navigate</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={actions.handleViewOnMap}
          className="items-center space-x-2"
        >
          <MapIcon className="size-4" />
          <span>View on Map</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={actions.handleDuplicate}
          className="items-center space-x-2"
        >
          <CopyIcon className="size-4" />
          <span>Duplicate</span>
        </DropdownMenuItem>
        {species && (
          <DropdownMenuItem
            onClick={handleViewSpecies}
            className="items-center space-x-2"
          >
            <LeafIcon className="size-4" />
            <span>View Species</span>
          </DropdownMenuItem>
        )}
        {"handleObserve" in actions && (
          <DropdownMenuItem
            onClick={actions.handleObserve}
            className="items-center space-x-2"
          >
            <EyeIcon className="size-4" />
            <span>Observe</span>
          </DropdownMenuItem>
        )}
        {hasCollection && (
          <DropdownMenuItem
            onClick={handleViewCollection}
            className="items-center space-x-2"
          >
            <TagIcon className="size-4" />
            <span>View Original Collection</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ObservationDetail() {
  const { id: projectId, observationId } = Route.useParams()

  const { data: observation, isPending: isObservationPending } = useObservation(
    {
      observationId,
      projectId,
    },
  )

  const { data: speciesDirect } = useSpecies(
    observation?.species_id ?? undefined,
  )
  const { data: collection } = useCollection({
    collectionId: observation?.collection_id ?? undefined,
    projectId,
  })
  const { data: speciesCollection } = useSpecies(
    collection?.determined_species_id ?? undefined,
  )

  const {
    data: { project, people, surveySites },
  } = useProjectData()

  const species = speciesDirect ?? speciesCollection
  const { data: alaData } = useALASpeciesDetail(species?.ala_guid)
  const consStatus =
    species?.metadata?.stateConservation?.WA?.status ??
    alaData?.conservationStatuses?.WA?.status

  const { data: photos } = usePhotosForEntity({
    entityId: observationId,
    entityType: "observation",
  })

  const observer = people?.find((p) => p.id === observation?.created_by)
  const surveySite = surveySites?.find(
    (s) => s.id === observation?.survey_site_id,
  )

  const location = observation?.location_coordinate
    ? parsePostGISPoint(observation.location_coordinate)
    : null

  const distanceKm = useDisplayDistance({
    latitude: location?.latitude ?? 0,
    longitude: location?.longitude ?? 0,
  })

  const navigate = useNavigate()

  const handleEdit = useCallback(() => {
    navigate({
      to: "/projects/$id/observations/$observationId/edit",
      params: { id: projectId, observationId },
    })
  }, [navigate, projectId, observationId])

  const handleAddDetermination = useCallback(() => {
    if (!observation?.collection_id) return
    navigate({
      to: "/projects/$id/collections/$collectionId/determinations/new",
      params: { id: projectId, collectionId: observation.collection_id },
      search: { observationId },
    })
  }, [navigate, projectId, observation?.collection_id, observationId])

  const peopleMap = useMemo(() => {
    return (
      people?.reduce(
        (acc, person) => {
          acc[person.id] = person
          return acc
        },
        {} as Record<string, Person>,
      ) ?? {}
    )
  }, [people])

  const recorderNames =
    observation?.recorders && observation.recorders.length > 0
      ? observation.recorders
          .map((id) => peopleMap[id]?.name ?? "Unknown")
          .join(", ")
      : (observer?.name ?? "Unknown")

  if (isObservationPending) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    )
  }

  if (!observation) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">Observation not found</p>
      </div>
    )
  }

  const speciesName = species?.name
  // The name above comes from the collection's determination when there is
  // one, so the uncertainty flag has to follow the same source.
  const speciesIndeterminate = observation.collection_id
    ? Boolean(collection?.determined_species_indeterminate)
    : Boolean(observation.species_indeterminate)

  return (
    <div className="flex flex-col gap-4">
      {/* Species / Collection Header */}
      <div className="flex flex-col gap-1">
        <div className="flex w-full flex-col gap-1">
          {speciesName && (
            <div className="flex flex-col gap-1">
              <div className="items-end-safe flex w-full justify-between">
                <div className="flex items-center gap-2">
                  <TaxonName className="text-xl" name={speciesName} />
                  {speciesIndeterminate && <IndeterminateMarker />}
                  {observation.is_collection && (
                    <IconPopover
                      label="Specimen collected"
                      icon={<TagIcon className="size-5" />}
                    >
                      Specimen collected
                    </IconPopover>
                  )}
                  {consStatus && (
                    <IconPopover
                      label="Conservation status"
                      icon={
                        <LucideTriangleAlert className="size-5 text-orange-500" />
                      }
                    >
                      {consStatus}
                    </IconPopover>
                  )}
                </div>
                <SyncStatusBadge entityId={observationId} />
              </div>
              <CollectionReference
                className="text-muted-foreground"
                collection={collection}
              />
            </div>
          )}
          {!speciesName && collection && (
            <div className="flex w-full items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <CollectionReference
                  className="text-xl"
                  collection={collection}
                />
                {observation.is_collection && (
                  <IconPopover
                    label="Specimen collected"
                    icon={<TagIcon className="size-5" />}
                  >
                    Specimen collected
                  </IconPopover>
                )}
              </div>
              <SyncStatusBadge entityId={observationId} />
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleEdit}
              className="items-center space-x-2"
            >
              <Pencil className="size-4" />
              <span>Edit</span>
            </Button>
            <ObservationActions observation={observation} />
          </div>
        </div>
      </div>

      {/* Sync Error Banner */}
      <SyncErrorBanner entityId={observationId} onEdit={handleEdit} />

      {/* Photos */}
      {photos && photos.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-muted-foreground text-sm">Photos</span>
          <Suspense
            fallback={
              <div className="grid grid-cols-4 gap-2">
                {photos.map((p) => (
                  <div
                    key={p.id}
                    className="bg-muted aspect-square animate-pulse rounded"
                  />
                ))}
              </div>
            }
          >
            <PhotoGrid photoIds={photos.map((p) => p.id)} />
          </Suspense>
        </div>
      )}

      {/* Details Section */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 rounded border p-1">
          <div className="flex justify-between">
            {/* Recorders */}
            <DetailRow label="Recorders" value={recorderNames} />
            {/* Date */}
            <DetailRow
              label="Date"
              value={
                observation.created_at
                  ? new Date(observation.created_at).toLocaleDateString(
                      undefined,
                      {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      },
                    )
                  : "Unknown"
              }
            />
            {/* Time */}
            <DetailRow
              label="Time"
              value={
                observation.created_at
                  ? new Date(observation.created_at).toLocaleTimeString(
                      undefined,
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )
                  : "Unknown"
              }
            />
          </div>
          <div className="flex justify-between">
            {/* Survey Site */}
            {surveySite && (
              <DetailRow
                label="Survey Site"
                value={
                  <Link
                    to={"/projects/$id/data/sites/$siteId"}
                    params={{ id: projectId, siteId: surveySite.id }}
                    search={{ tab: "data" }}
                  >
                    {surveySite.name}
                  </Link>
                }
              />
            )}

            {/* Location */}
            {location && (
              <DetailRow
                label="Location"
                value={
                  <div className="flex items-center gap-2">
                    <MapPin className="text-muted-foreground size-4" />
                    <span className="flex flex-col">
                      <CoordinateDisplay
                        value={location}
                        crs={project?.location_crs}
                      />
                      {distanceKm && (
                        <span className="text-muted-foreground text-sm">
                          {distanceKm} km away
                        </span>
                      )}
                    </span>
                  </div>
                }
              />
            )}
          </div>
        </div>

        {/* Notes */}
        {observation.notes && (
          <div className="flex flex-col gap-1">
            <span className="text-muted-foreground text-sm">Notes</span>
            <p className="text-foreground whitespace-pre-wrap">
              {observation.notes}
            </p>
          </div>
        )}

        {/* Determination (for collection observations) */}
        {collection && observation.is_collection && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-sm">
                Determination
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddDetermination}
                className="items-center gap-1"
              >
                <Microscope className="size-4" />
                <span>
                  {collection.determined_species_id
                    ? "Re-determine"
                    : "Add Determination"}
                </span>
              </Button>
            </div>
            {collection.determined_species_name ? (
              <div className="bg-muted/20 rounded-lg border p-3">
                <div className="flex items-center gap-2">
                  <TaxonName
                    className="text-lg"
                    name={collection.determined_species_name}
                  />
                  {collection.determined_species_indeterminate && (
                    <IndeterminateMarker size="sm" />
                  )}
                </div>
                {collection.determined_at && (
                  <div className="text-muted-foreground text-sm">
                    Identified on{" "}
                    {new Date(collection.determined_at).toLocaleDateString()}
                  </div>
                )}
                {collection.determination_notes && (
                  <div className="text-muted-foreground mt-1 text-sm">
                    {collection.determination_notes}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted-foreground text-sm italic">
                Unidentified
              </div>
            )}
          </div>
        )}

        {/* Custom Fields */}
        {observation.custom_fields &&
          Object.keys(observation.custom_fields).length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(getFormattedCustomFields(observation, project))
                  .filter(
                    ([, { value }]) =>
                      value !== null && value !== undefined && value !== "",
                  )
                  .map(([key, { displayValue, label }]) => {
                    return (
                      <DetailRow key={key} label={label} value={displayValue} />
                    )
                  })}
              </div>
            </div>
          )}
      </div>
    </div>
  )
}

function DetailRow({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-muted-foreground text-sm">{label}</span>
      <div className="text-foreground">{value}</div>
    </div>
  )
}

